import { Box, visuallyHidden } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { TEACHER_COLOR } from "@outschool/ui-components-classroom";
import { useComponentTrackingContext } from "@outschool/ui-legacy-component-library";
import React, { useEffect } from "react";

import { SearchSuggestionsMenu } from "../../../../shared/client/components/find-classes/SearchSuggestionsMenu";
import { ExtendedSearchFiltersType } from "../../../lib/useSearchFilters";
import ClassSearchForm from "../ClassSearchForm";
import useSearchWithSuggestions, {
  queryFromSearchItem
} from "../useSearchWithSuggestions";

export default function DesktopSearchField({
  searchPlaceholder,
  searchFilters,
  setSearchFilters,
  setImplicitSearchFilters,
  clearDropdowns,
  useGlobalNavUi,
  useTutoringSearchUi
}: {
  searchPlaceholder?: string;
  searchFilters: ExtendedSearchFiltersType;
  setSearchFilters: (filters: ExtendedSearchFiltersType) => void;
  setImplicitSearchFilters: (filters: ExtendedSearchFiltersType) => void;
  clearDropdowns?: () => void;
  useGlobalNavUi?: boolean;
  useTutoringSearchUi?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\search\\Desktop\\DesktopSearchField"
  );
  const track = useComponentTrackingContext();

  const {
    getComboboxProps,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    inputValue,
    setInputValue,
    onInputFocus,
    loadingSuggestions,
    suggestionsOpen,
    searchSuggestions,
    suggestionsHighlightedIndex,
    closeMenu
  } = useSearchWithSuggestions(
    item => {
      setImplicitSearchFilters(queryFromSearchItem(item, searchFilters));
    },
    item => {
      setSearchFilters(queryFromSearchItem(item, searchFilters));
    },
    // CHECK AB TEST
    // @ts-ignore TS(2322): Type 'string' is not assignable to type 'never'.
    { ignore: useGlobalNavUi ? ["teachers"] : [] },
    useGlobalNavUi
  );

  useEffect(() => {
    // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    setInputValue(searchFilters.q || searchFilters.userName || null);
  }, [setInputValue, searchFilters.q, searchFilters.userName]);

  const isTeacherSearch = !!searchFilters.userUid;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        position: "relative",
        "* > input": {
          color: isTeacherSearch ? TEACHER_COLOR : undefined,
          fontWeight: isTeacherSearch ? 500 : "unset"
        },
        ...(useGlobalNavUi || useTutoringSearchUi ? { width: "100%" } : {})
      }}
      {...getComboboxProps()}
    >
      <span style={visuallyHidden} {...getLabelProps()}>
        {t("Search for classes:")}
      </span>
      <ClassSearchForm
        query={inputValue}
        setQuery={setInputValue}
        placeholder={searchPlaceholder}
        onInputFocus={() => {
          // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          clearDropdowns();
          onInputFocus();
        }}
        getInputProps={getInputProps}
        onSubmit={query => {
          if (suggestionsOpen) {
            closeMenu();
          }
          // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          clearDropdowns();
          setSearchFilters(
            queryFromSearchItem(
              {
                type: "keyword",
                name: query || undefined,
                uid: ""
              },
              searchFilters
            )
          );
        }}
        spellCheck={!searchFilters.userUid}
        useGlobalNavUi={useGlobalNavUi}
        useTutoringSearchUi={useTutoringSearchUi}
      />
      <SearchSuggestionsMenu
        getMenuProps={getMenuProps}
        isOpen={suggestionsOpen}
        searchSuggestions={searchSuggestions}
        loadingSuggestions={loadingSuggestions}
        highlightedIndex={suggestionsHighlightedIndex}
        getItemProps={getItemProps}
        keyword={inputValue}
        menuStyle={{
          top: "40px",
          minWidth: 291,
          width: "100%",
          ...(useGlobalNavUi ? { zIndex: 1000 } : {})
        }}
        track={track}
        useGlobalNavUi={useGlobalNavUi}
      />
    </Box>
  );
}
